import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef,} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { State } from '../../../models/state';
import { County } from '../../../models/county';
import { GeneralDataService } from '../../../services/GeneralDataService';
import { firstValueFrom, forkJoin, map, switchMap, take } from 'rxjs';
 
export interface StateCountySelection {
  stateCode: string;
  counties: number[]; // countyId[]
}
 
interface StateViewModel {
  state: State;
  counties: County[];
  filteredCounties: County[];
  isOpen: boolean;
  selectedCountyIds: Set<number>;
}
 
@Component({
  selector: 'app-state-county-selector',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './state-county-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./state-county-selector.component.scss'],
})
export class StateCountySelectorComponent {
 
  @Input() initialSelection: Record<string,number[]> | undefined;
  @Output() selectionChange = new EventEmitter<Record<string,number[]>>();
 
  public stateViewModels: StateViewModel[] = [];
  public searchQuery: string|undefined = undefined;

  constructor(private gdService:GeneralDataService, private cd:ChangeDetectorRef){

  }

  async ngAfterViewInit(){
    await this.buildViewModels();
  }
 
  get filteredStateViewModels(): StateViewModel[] {
    const q = this.searchQuery?.trim().toLowerCase();
    if (!q) return this.stateViewModels;
    return this.stateViewModels.filter(vm =>
      vm.state.name.toLowerCase().includes(q) ||
      vm.state.stateCode.toLowerCase().includes(q)
    );
  }
 
  get totalSelectedStates(): number {
    return this.stateViewModels.filter(vm => vm.selectedCountyIds.size > 0).length;
  }
 
  get totalSelectedCounties(): number {
    return this.stateViewModels.reduce((sum, vm) => sum + vm.selectedCountyIds.size, 0);
  }
  
  private async buildViewModels(): Promise<void> {
    let stateViewModels: StateViewModel[]; // adjust type name to whatever it actually is
    try {
      stateViewModels = await firstValueFrom(
        this.gdService.getStates().pipe(
          take(1),
          switchMap((states: State[]) =>
            forkJoin(
              states.map(state =>
                this.gdService.getCounties(state.id).pipe(
                  take(1),
                  map((counties: County[]) => ({
                    state,
                    counties,
                    filteredCounties: counties,
                    isOpen: false,
                    selectedCountyIds: new Set<number>(),
                  }))
                )
              )
            )
          )
        )
      );
    } catch {
      // Session ended mid-load (token refresh failed) or a real fetch error -
      // either way there's no usable view model to build. Leave it empty and
      // bail; nothing downstream should run against a half-built state.
      this.stateViewModels = [];
      return;
    }

    // assign once (important for OnPush)
    this.stateViewModels = stateViewModels;

    // apply initial selection
    if (this.initialSelection) {
      for (const [key, value] of Object.entries(this.initialSelection)) {
        const vm = this.stateViewModels.find(v => v.state.stateCode === key);
        if (vm) {
          vm.selectedCountyIds = new Set(value);
          if (vm.selectedCountyIds.size > 0) vm.isOpen = true;
        }
      }
    }
  }
 
  // ── Accordion ────────────────────────────────────────────────
 
  toggleState(vm: StateViewModel): void {
    vm.isOpen = !vm.isOpen;
  }
 
  // ── State-level checkbox ──────────────────────────────────────
 
  getStateCheckState(vm: StateViewModel): 'none' | 'partial' | 'all' {
    const sel = vm.selectedCountyIds.size;
    if (sel === 0) return 'none';
    if (sel === vm.counties.length) return 'all';
    return 'partial';
  }
 
  toggleAllCounties(vm: StateViewModel, event: Event): void {
    event.stopPropagation();
    if (this.getStateCheckState(vm) === 'all') {
      vm.selectedCountyIds.clear();
    } else {
      vm.selectedCountyIds = new Set(vm.counties.map(c => c.id));
    }
    this.emit();
  }
 
  // ── County-level checkbox ─────────────────────────────────────
 
  isCountySelected(vm: StateViewModel, countyId: number): boolean {
    return vm.selectedCountyIds.has(countyId);
  }
 
  toggleCounty(vm: StateViewModel, countyId: number): void {
    if (vm.selectedCountyIds.has(countyId)) {
      vm.selectedCountyIds.delete(countyId);
    } else {
      vm.selectedCountyIds.add(countyId);
    }
    this.emit();
  }
 
  // ── Emit ──────────────────────────────────────────────────────
 
  private emit(): void {
    const selection: Record<string, number[]> = this.stateViewModels
      .filter(vm => vm.selectedCountyIds.size > 0)
      .reduce((acc, vm) => {
        acc[vm.state.stateCode] = Array.from(vm.selectedCountyIds);
        return acc;
      }, {} as Record<string, number[]>);

    this.selectionChange.emit(selection);
  }
 
  // ── Clear all ─────────────────────────────────────────────────
 
  clearAll(): void {
    this.stateViewModels.forEach(vm => vm.selectedCountyIds.clear());
    this.emit();
  }
}
 